<template>
	<div ref="root" class="order-details" :class="`order-details--${variant}`">
		<!-- Drawer (Figma "Header/ Drawer", 14254:161694): 96 tall, the back tile left, the one action right. It
		     stays at the top while the drawer's panel scrolls. -->
		<div v-if="variant === 'drawer'" class="order-details__bar">
			<button type="button" class="order-details__back-tile" :aria-label="COPY.back" @click="emit('close')">
				<Icon type="ds_arrow_left" :size="ARROW_SIZE.tile" aria-hidden="true" />
			</button>
			<Button v-if="track" class="order-details__bar-action" @ds:click="track.open">
				<Text font="--text-16-18-semi">{{ track.label }}</Text>
			</Button>
		</div>

		<!-- Page (Figma "Top", 14254:162875): "← All orders / Order #N" with the badge on the right; the order
		     number is the page's level-1 heading. -->
		<div v-else class="order-details__crumbs">
			<div class="order-details__trail">
				<Button
					class="order-details__crumb-back"
					hierarchy="tertiary"
					color="brand"
					font="--text-16-22-semi"
					@ds:click="emit('close')"
				>
					<template #icon-left>
						<Icon type="ds_arrow_left" :size="ARROW_SIZE.crumb" aria-hidden="true" />
					</template>
					<Text font="--text-16-22-semi">{{ COPY.allOrders }}</Text>
				</Button>
				<Text tag="span" font="--text-16-22-semi" :color="KOVA.textSecondary" aria-hidden="true">/</Text>
				<Text
					:id="headingId"
					class="order-details__heading"
					tag="span"
					font="--text-16-22-semi"
					:color="KOVA.textSecondary"
					role="heading"
					aria-level="1"
					tabindex="-1"
				>
					{{ model ? `Order #${model.orderNumber}` : DETAIL_COPY.title }}
				</Text>
			</div>
			<Badge v-if="model?.badge" :tone="BADGE_TONE[model.badge.tone]" size="sm">{{ model.badge.label }}</Badge>
		</div>

		<div class="order-details__body">
			<!-- The drawer is labelled by its heading: until the order is there, an unseen one names it. -->
			<Text
				v-if="variant === 'drawer' && !model"
				:id="headingId"
				class="order-details__heading order-details__unseen"
				font="--text-16-22-semi"
				role="heading"
				aria-level="1"
				tabindex="-1"
			>
				{{ DETAIL_COPY.title }}
			</Text>

			<OrderDetailsSkeleton v-if="view === 'loading'" />

			<!-- Not this user's order, not an order at all, or gone (404 / 400 / an id that isn't a UUID): no retry. -->
			<LayoutFlex
				v-else-if="view === 'unavailable'"
				padding="var(--size-24)"
				gap="var(--size-16)"
				background="--background-white"
				corner-radius="var(--size-36)"
			>
				<LayoutFlex role="alert" padding="0" gap="var(--size-8)">
					<Text font="--text-18-22-semi" color="--text-dark-primary">{{ COPY.unavailableTitle }}</Text>
					<Text font="--text-14-20-med" color="--text-dark-secondary">{{ COPY.unavailableBody }}</Text>
				</LayoutFlex>
				<Button full-width @ds:click="emit('close')">
					<Text font="--text-16-18-semi">{{ COPY.back }}</Text>
				</Button>
			</LayoutFlex>

			<!-- The read broke (5xx, no network) and there is nothing cached to show. -->
			<LayoutFlex
				v-else-if="view === 'failed'"
				padding="var(--size-24)"
				gap="var(--size-16)"
				background="--background-white"
				corner-radius="var(--size-36)"
			>
				<LayoutFlex role="alert" padding="0" gap="var(--size-8)">
					<Text font="--text-18-22-semi" color="--text-dark-primary">{{ COPY.errorTitle }}</Text>
					<Text font="--text-14-20-med" color="--text-dark-secondary">{{ COPY.errorBody }}</Text>
				</LayoutFlex>
				<LayoutFlex padding="0" gap="var(--size-8)">
					<Button full-width @ds:click="reload">
						<Text font="--text-16-18-semi">{{ COPY.tryAgain }}</Text>
					</Button>
					<Button full-width hierarchy="secondary" @ds:click="emit('close')">
						<Text font="--text-16-18-semi">{{ COPY.back }}</Text>
					</Button>
				</LayoutFlex>
			</LayoutFlex>

			<template v-else-if="model">
				<!-- A background re-read failed: the order stays, with an honest note and a way to retry. -->
				<LayoutFlex
					v-if="entry?.refreshFailed"
					padding="var(--size-20) var(--size-24)"
					gap="var(--size-12)"
					background="--background-white"
					corner-radius="var(--size-36)"
					align-items="flex-start"
				>
					<Text role="alert" font="--text-14-20-med" color="--text-dark-primary">{{ COPY.staleBody }}</Text>
					<Button hierarchy="secondary" :loading="refreshing" @ds:click="refresh">
						<Text font="--text-16-18-semi">{{ COPY.tryAgain }}</Text>
					</Button>
				</LayoutFlex>

				<OrderDetailsContent :model="model" :variant="variant" :heading-id="headingId" />

				<!-- Figma 14254:163882: full width, 48 tall, over the cards at the bottom of the scroll area — sticky,
				     never fixed (docs/architecture/portal.md: no position:fixed in the outlet). -->
				<div v-if="variant === 'page' && track" class="order-details__sticky-action">
					<Button full-width @ds:click="track.open">
						<Text font="--text-16-18-semi">{{ track.label }}</Text>
					</Button>
				</div>
			</template>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { Badge, Button, Icon, LayoutFlex, Text } from '@simple-life-app/growth-ui-kova'
import { KOVA } from '@/designTokens'
import { openTracking } from '@/orders/destinations'
import { buildOrderDetailModel, DETAIL_COPY } from '@/orders/detailModel'
import { useOrdersStore } from '@/store/orders'
import OrderDetailsContent from './OrderDetailsContent.vue'
import OrderDetailsSkeleton from './OrderDetailsSkeleton.vue'
import { BADGE_TONE } from './orderGlyphs'

/**
 * One order's details (KOVA-797, spec §3.5 / §6.2), the same component as the ≥ 1024px right drawer
 * (`variant="drawer"`, Figma 14254:161609 …) and the page in the section's outlet below it (`"page"`, Figma
 * 14226:100203 …). It reads the order through the Orders store — on mount and whenever `orderId` changes —
 * and draws `buildOrderDetailModel`'s model; the host owns where "back" goes and hears it as `close`.
 *
 * States: loading (the skeleton), unavailable (no retry), failed with nothing cached (Try again), the order,
 * and the order with a failed background re-read (the stale note). `headingId` names the level-1 heading in
 * every state, so a drawer labelled by it is never unnamed. No order number, address, phone or tracking
 * link in any attribute (docs/architecture/sentry.md → PHI/PII hardening).
 */

const props = defineProps<{
	orderId: string
	variant: 'page' | 'drawer'
	/** The id of the details' level-1 heading — what the host's drawer is labelled by. Keep it PII-free. */
	headingId: string
}>()

const emit = defineEmits<{ close: [] }>()

// No Figma frame for the failure states (spec §6.2): the unavailable and stale copy is proposed; the error
// copy is the mobile app's `common.errorDefault` / `common.errorNetworkDescription` / `common.tryAgain`.
const COPY = {
	back: 'Back to all orders',
	allOrders: 'All orders',
	unavailableTitle: 'We can’t find this order',
	unavailableBody: 'It may have been removed or belong to another account.',
	errorTitle: 'Something went wrong',
	errorBody: 'Make sure you’re connected to the internet and try again',
	staleBody: 'We couldn’t refresh this order. What you see may be out of date.',
	tryAgain: 'Try again',
} as const

/**
 * The back arrow's DS `Icon` size: Figma draws a 20px arrow on the drawer's tile and a 16px one in the
 * breadcrumb. The DS glyph sits in a 16 × 17 box, which a square `Icon` scales by its height, so each is 2px
 * up to come out at Figma's length and stroke.
 */
const ARROW_SIZE = {
	tile: 'calc(var(--size-20) + var(--size-2))',
	crumb: 'calc(var(--size-16) + var(--size-2))',
} as const

const store = useOrdersStore()

const root = ref<HTMLElement | null>(null)

const entry = computed(() => store.details[props.orderId])
const model = computed(() => (entry.value?.order ? buildOrderDetailModel(entry.value.order) : null))

/** What the body shows. Nothing read yet counts as loading (the read starts with the component). */
const view = computed<'loading' | 'unavailable' | 'failed' | 'order'>(() => {
	if (entry.value?.order) return 'order'
	if (entry.value?.state === 'unavailable') return 'unavailable'
	if (entry.value?.state === 'failed') return 'failed'
	return 'loading'
})

/** The stale note's Try again, while its re-read is in flight. */
const refreshing = ref(false)

watch(
	() => props.orderId,
	(id) => {
		refreshing.value = false
		void store.loadOrder(id)
	},
	{ immediate: true },
)

/**
 * After a retry the control that was clicked is gone (the error card, the stale note): rather than leave the
 * focus on the page body — outside the drawer's trap — put it on the details' heading, where reading resumes.
 */
async function refocusIfLost() {
	await nextTick()
	const active = document.activeElement
	if (active && active !== document.body) return
	const heading = document.getElementById(props.headingId)
	if (heading && root.value?.contains(heading)) heading.focus({ preventScroll: true })
}

/** The failed state's Try again: the skeleton again, then the order or the error. */
async function reload() {
	await store.loadOrder(props.orderId)
	await refocusIfLost()
}

/** The stale note's Try again: the order stays on screen while it re-reads. */
async function refresh() {
	const id = props.orderId
	refreshing.value = true
	await store.refreshOrder(id)
	if (id !== props.orderId) return
	refreshing.value = false
	await refocusIfLost()
}

/**
 * The one action, Track (an https link the model vetted): the carrier page in a new tab through its seam
 * (`openTracking`), synchronously in the click so no popup blocker stops it. The label is this variant's —
 * "Track order" in the drawer's header, the carrier's name on the page's button.
 */
const track = computed<{ label: string; open: () => void } | null>(() => {
	const action = model.value?.track
	if (!action) return null
	const url = action.url
	return { label: action.label[props.variant], open: () => openTracking(url) }
})
</script>

<style scoped>
.order-details {
	display: flex;
	flex-direction: column;
}

.order-details--page {
	gap: var(--size-20);
}

.order-details__body {
	display: flex;
	flex-direction: column;
	gap: var(--size-8);
}

/* The drawer's panel has no padding of its own (DS Drawer): the header 24 in, the cards 24 in and 24 above
   the panel's bottom edge (Figma: a 570 column in the 618 drawer). */
.order-details--drawer .order-details__body {
	padding: 0 var(--size-24) var(--size-24);
}

.order-details__bar {
	position: sticky;
	top: 0;
	z-index: 1;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--size-16);
	height: var(--size-96);
	padding: 0 var(--size-24);
	background: var(--background-base);
}

/* Figma "Button base": a white 48px tile, radius 20, the 20px arrow in the brand green. No DS equivalent:
   the DS ButtonIcon tiles are 28px, PortalIconButton's 40. */
.order-details__back-tile {
	display: flex;
	flex: none;
	align-items: center;
	justify-content: center;
	width: var(--size-48);
	height: var(--size-48);
	padding: 0;
	border: 0;
	border-radius: var(--size-20);
	background: var(--background-white);
	color: var(--text-accent-brand);
	cursor: pointer;
}

.order-details__back-tile:focus-visible {
	outline: 2px solid var(--stroke-accent-brand);
	outline-offset: 2px;
}

/* Figma: the action is 24 in from its sides (the DS Button pads 12). */
.order-details__bar .order-details__bar-action {
	padding: 0 var(--size-24);
}

.order-details__crumbs {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--size-12);
}

.order-details__trail {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: var(--size-4);
	min-width: 0;
}

/* The breadcrumb's link look: the DS tertiary button without its 12px padding, so the arrow lines up with the
   cards' edge. */
.order-details__trail .order-details__crumb-back {
	gap: var(--size-4);
	padding: 0;
}

/* Figma 14254:163882: 28 above the bottom of the screen. */
.order-details__sticky-action {
	position: sticky;
	bottom: var(--size-28);
	z-index: 1;
}

/* A focus target after a retry only (tabindex -1), not a control: no ring. */
.order-details__heading:focus {
	outline: none;
}

.order-details__unseen {
	position: absolute;
	width: 1px;
	height: 1px;
	overflow: hidden;
	clip-path: inset(50%);
	white-space: nowrap;
}
</style>
